// src/lib/SyncService.js
import AsyncStorage from '@react-native-async-storage/async-storage';
import api from './api';
import StorageManager from '../utils/StorageManager';
import AuthService from '../services/authService';

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.syncInterval = null;
    this.lastSyncTime = null;
    this.syncQueue = [];
    this.maxRetries = 3; // Máximo de tentativas
    this.retryCount = new Map(); // Contador de tentativas por item
  }

  // Obter status atual
  getStatus() {
    return {
      isSyncing: this.isSyncing,
      lastSyncTime: this.lastSyncTime,
      queueLength: this.syncQueue.length,
    };
  }

  // Verificar conexão com servidor
  async checkServerConnection() {
    try {
      const result = await api.checkServerHealth();
      return result.status === 'online';
    } catch (error) {
      console.error('Erro ao verificar conexão:', error);
      return false;
    }
  }

  // Iniciar sincronização automática
  startAutoSync(intervalMinutes = 15) {
    // Não sincronizar se for visitante
    if (AuthService.isVisitorMode()) {
      console.log('🚫 Sincronização automática desabilitada para visitantes');
      return;
    }

    // Parar sincronização anterior se existir
    this.stopAutoSync();

    // Sincronizar imediatamente
    this.syncNow();

    // Configurar intervalo
    const intervalMs = intervalMinutes * 60 * 1000;
    this.syncInterval = setInterval(() => {
      this.syncNow();
    }, intervalMs);

    console.log(`✅ Sincronização automática iniciada (a cada ${intervalMinutes} minutos)`);
  }

  // Parar sincronização automática
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('🛑 Sincronização automática parada');
    }
  }

  // Sincronizar agora
  async syncNow() {
    // Não sincronizar se for visitante
    if (AuthService.isVisitorMode()) {
      console.log('🚫 Sincronização não permitida para visitantes');
      return { success: false, error: 'Visitantes não podem sincronizar' };
    }

    if (this.isSyncing) {
      console.log('⏳ Sincronização já em andamento...');
      return { success: false, error: 'Sincronização já em andamento' };
    }

    this.isSyncing = true;
    console.log('🔄 Iniciando sincronização...');

    try {
      // Verificar conexão primeiro
      const isOnline = await this.checkServerConnection();
      if (!isOnline) {
        console.log('📵 Servidor offline, sincronização adiada');
        return { success: false, error: 'Servidor offline' };
      }

      // Sincronizar dados
      const results = {
        forms: await this.syncForms(),
        responses: await this.syncResponses(),
        pendingDeletes: await this.syncPendingDeletes(),
      };

      this.lastSyncTime = new Date();
      await AsyncStorage.setItem('last_sync_time', this.lastSyncTime.toISOString());

      console.log('✅ Sincronização concluída', results);
      return { success: true, results };
    } catch (error) {
      console.error('❌ Erro durante sincronização:', error);
      return { success: false, error: error.message };
    } finally {
      this.isSyncing = false;
    }
  }

  // Sincronizar formulários
  async syncForms() {
    try {
      // Buscar formulários do servidor
      const serverForms = await api.getForms();

      // Buscar formulários locais
      const localForms = await StorageManager.loadCustomForms();

      // Mesclar dados (servidor tem prioridade)
      const mergedForms = this.mergeForms(serverForms, localForms);

      // Salvar formulários mesclados
      await StorageManager.saveCustomForms(mergedForms);

      return {
        success: true,
        downloaded: serverForms.length,
        local: localForms.length,
        merged: mergedForms.length,
      };
    } catch (error) {
      console.error('Erro ao sincronizar formulários:', error);
      return { success: false, error: error.message };
    }
  }

  // Sincronizar respostas
  async syncResponses() {
    try {
      // Buscar respostas locais não sincronizadas
      const localResponses = await StorageManager.loadFormResponses();
      const unsyncedResponses = localResponses.filter(r => !r.synced);

      let uploaded = 0;
      let failed = 0;

      // Enviar cada resposta não sincronizada
      for (const response of unsyncedResponses) {
        // Verificar se a resposta tem anexos com base64
        let hasLargeAttachments = false;
        if (response.attachments) {
          Object.values(response.attachments).forEach(attachmentList => {
            attachmentList.forEach(att => {
              if (att.base64 && att.base64.length > 1000000) {
                // > 1MB
                hasLargeAttachments = true;
              }
            });
          });
        }

        if (hasLargeAttachments) {
          console.log(
            `⚠️ Resposta ${response.id} tem anexos grandes, pulando sincronização automática`
          );
          continue;
        }
        try {
          const getRetryKey = () => `response_${response.id}`;
          const retryKey = getRetryKey();
          const retries = this.retryCount.get(retryKey) || 0;

          if (retries >= this.maxRetries) {
            console.log(
              `⏭️ Pulando resposta ${response.id} após ${this.maxRetries} tentativas falhas`
            );
            failed++;
            continue;
          }

          await api.saveResponse(response);
          uploaded++;

          // Limpar contador de tentativas em caso de sucesso
          this.retryCount.delete(retryKey);
        } catch (error) {
          const retryKey = `response_${response.id}`;
          const retries = this.retryCount.get(retryKey) || 0;
          this.retryCount.set(retryKey, retries + 1);

          console.error(
            `Erro ao sincronizar resposta ${response.id} (tentativa ${retries + 1}):`,
            error
          );
          failed++;
        }
      }

      return {
        success: true,
        total: unsyncedResponses.length,
        uploaded,
        failed,
      };
    } catch (error) {
      console.error('Erro ao sincronizar respostas:', error);
      return { success: false, error: error.message };
    }
  }

  // CORREÇÃO: Sincronizar exclusões pendentes com limite de tentativas
  async syncPendingDeletes() {
    try {
      const pendingDeletes = await AsyncStorage.getItem('pending_response_deletes');
      if (!pendingDeletes) {
        return { success: true, deleted: 0, failed: 0 };
      }

      const deletes = JSON.parse(pendingDeletes);
      if (deletes.length === 0) {
        return { success: true, deleted: 0, failed: 0 };
      }

      console.log(`🔄 Processando ${deletes.length} exclusões pendentes...`);

      let deleted = 0;
      let failed = 0;
      const failedDeletes = [];

      for (const del of deletes) {
        const retryKey = `delete_${del.id}`;
        const retries = this.retryCount.get(retryKey) || 0;

        // Se já tentou muitas vezes, pular este item
        if (retries >= this.maxRetries) {
          console.log(`⏭️ Pulando exclusão ${del.id} após ${this.maxRetries} tentativas falhas`);
          failed++;
          continue; // Não adicionar de volta à lista
        }

        try {
          await api.deleteResponse(del.id);
          deleted++;
          // Limpar contador de tentativas em caso de sucesso
          this.retryCount.delete(retryKey);
        } catch (error) {
          // Se o erro for 404, considerar como sucesso (já foi deletado)
          if (error.message.includes('404') || error.message.includes('não encontrada')) {
            console.log(`✅ Resposta ${del.id} já foi excluída do servidor`);
            deleted++;
            this.retryCount.delete(retryKey);
          } else {
            // Incrementar contador de tentativas
            this.retryCount.set(retryKey, retries + 1);
            console.error(`Erro ao excluir ${del.id} (tentativa ${retries + 1}):`, error);
            failed++;
            // Adicionar de volta apenas se não excedeu o limite
            if (retries + 1 < this.maxRetries) {
              failedDeletes.push(del);
            }
          }
        }
      }

      // Salvar apenas as exclusões que falharam e ainda têm tentativas restantes
      if (failedDeletes.length > 0) {
        await AsyncStorage.setItem('pending_response_deletes', JSON.stringify(failedDeletes));
      } else {
        // Se não há mais nada para deletar, limpar a lista
        await AsyncStorage.removeItem('pending_response_deletes');
      }

      return { success: true, deleted, failed };
    } catch (error) {
      console.error('Erro ao processar exclusões pendentes:', error);
      return { success: false, error: error.message };
    }
  }

  // Mesclar formulários (helper)
  mergeForms(serverForms, localForms) {
    const merged = new Map();

    // Adicionar formulários do servidor
    serverForms.forEach(form => {
      merged.set(form.id, { ...form, synced: true });
    });

    // Adicionar/atualizar com formulários locais que não estão no servidor
    localForms.forEach(form => {
      if (!merged.has(form.id)) {
        merged.set(form.id, form);
      }
    });

    return Array.from(merged.values());
  }

  // Limpar cache de tentativas antigas
  clearOldRetries() {
    // Limpar entradas com mais de 24 horas
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const keysToDelete = [];

    this.retryCount.forEach((value, key) => {
      // Implementar lógica de timestamp se necessário
      // Por enquanto, limpar tudo ao reiniciar
    });

    keysToDelete.forEach(key => this.retryCount.delete(key));
  }
}

// Exportar instância única
export default new SyncService();
